import React, { useState, useEffect, useRef } from 'react';

import TimecardStatusModal from './TimecardStatusModal';
import TimecardStatus from './TimecardStatus';
import './Timecard.css';
import { SingleValue, ActionMeta } from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { appendAlert } from "../../redux/alertSlice"
import apiAxios from '../../utils/apiAxios'
import { dateToTimestampString } from '../../utils/utils'

import { ProcCodeSelect } from '../ui/ProcCodeSelect';
import { MachCodeSelect } from '../ui/MachCodeSelect';
import { TeamSelect } from '../ui/TeamSelect';
import { MemberSelectWithWorkStatus } from '../ui/MemberSelectWithWorkStatus';
import { SuspendReasonSelect } from '../ui/SuspendReasonSelect';

import { GetWorkStatus } from "../../models/GetWorkStatus";
import { PostWorkStatus } from "../../models/PostWorkStatus";
import Spinner from '../../components/ui/Spinner';

type Props = { };

const Timecard: React.FC<Props> = () => {

  const dispatch = useDispatch();

  // storeの値を取得
  const plant_team_member = useSelector((state:RootState) => state.member.plant_team_member);           // 作業者リスト
  const plantCode = useSelector((state: RootState) => state.queryParams.selectedPlantCode);             // 工場コード
  const procCode = useSelector((state: RootState) => state.queryParams.selectedProcCode);               // 加工コード
  const machCode = useSelector((state: RootState) => state.queryParams.machCode);                       // 機械作業コード
  const userId = useSelector((state: RootState) => state.user.userInfo?.user_id ?? "");                              // ユーザーID

  // 加工に対する所属が存在するか判定するユーティリティ
  const hasTeamByProcCode = (proc_code: string) => {
    return plant_team_member.find(d => d.plant_code === plantCode)?.teams.find(d => d.proc_code === proc_code) ? true : false;
  }

  // 初期値を設定
  const [openModalTime] = useState<Date>(new Date()); // 現在の時刻
  const [selectedProcCode, setSelectedProcCode] = useState<string>(procCode); // 加工コード
  const [selectedMachCode, setSelectedMachCode] = useState<string>(machCode && machCode.length === 1 ? machCode[0] : ""); // 機械作業コード
  const [selectedTeamCode, setSelectedTeamCode] = useState<string>(hasTeamByProcCode(procCode) ? procCode : "");       // 所属コード
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");       // 社員コード
  const [selectedDateTime, setSelectedDateTime] = useState<Date>(openModalTime);      // 開始時間 or 終了時間
  const [selectedSuspendId, setSelectedSuspendId] = useState<number>(0);      // 中断ID

  const [selectedWorkStatuses, setSelectedWorkStatuses] = useState<GetWorkStatus[]|null>([]); // の作業者状況(GET /work_statusesで取得したデータ)
  const [selectedWorkStatus, setSelectedWorkStatus] = useState<GetWorkStatus|null>(null); // 選択している作業者の作業者状況(GET /work_statusesで取得したデータ)

  const [isLoading, setIsLoading] = useState<boolean>(false); // ローディング中かどうかのフラグ
  const isPosting = useRef<boolean>(false); // ポスト中かどうかのフラグ

  // セレクトボックスの変更を検知しstateの状態を更新
  // 加工コードの変更を検知
  const handleProcCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProcCode(e.target.value);
    setSelectedMachCode("");
    if(hasTeamByProcCode(e.target.value)) {
      // 加工コードに対する所属が存在する場合は、所属コードを加工コードに設定
      setSelectedTeamCode(e.target.value);
      setSelectedMemberId("");
    } else {
      setSelectedTeamCode("");
      setSelectedMemberId("");
    }
  };
  // 機械作業コードの変更を検知
  const handleMachCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMachCode(e.target.value);
  };
  // 所属コードの変更を検知
  const handleTeamCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTeamCode(e.target.value);
    setSelectedMemberId("");
  };
  // 社員コードの変更を検知
  const handleMemberIdChange = (selectedOption: SingleValue<any>, actionMeta: ActionMeta<any>) => {
    setSelectedMemberId(selectedOption.value);
  };
  // 中断IDの変更を検知
  const handleSuspendIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSuspendId(Number(e.target.value));
  };

  // 画面制御用のステータスを取得
  type Status = "unSelected" | "idle" | "working" | "suspend" | "resume" | "loading";
  const getDisplayStatus = (workStatus: GetWorkStatus|null): Status => {
    if (isLoading) {
      return "loading";
    }
    if(workStatus === null) {
      return "unSelected";
    } else {
      switch(workStatus.status_code) {
        case 0:
          return "idle";
        case 1:
          return "working";
        case 2:
          return "suspend";
        case 3:
          return "resume";
        case 4: 
          return "idle";
        default:
          return "unSelected";
      }
    }
  }

  const updateDisplayWorkStatus = (workStatus: GetWorkStatus|null) => {
    if (workStatus) {
      // 画面状態の更新
      setSelectedWorkStatus(workStatus); // 作業者状況の更新
      setSelectedDateTime(openModalTime); // 画面を開いたとの時間を設定
      setSelectedSuspendId(workStatus.work_suspension_reason_code); // 中断理由の更新
     } else {
      // 画面状態のクリア
      setSelectedWorkStatus(null); // 作業者状況の初期化
      setSelectedDateTime(openModalTime); // ステータス時間の初期化
      setSelectedSuspendId(0); // 中断理由の初期化
     }
  };

  // 作業者状況を取得する
  const getFetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await apiAxios.get<GetWorkStatus[]>(
        `/work_statuses`
        , {
          params: {
            plant_code: plantCode,
            proc_code: selectedProcCode,
            mach_code: selectedMachCode,
          }
        }
      );
      setSelectedWorkStatuses(response.data);
    } catch (error:any){
      console.error(error);
      updateDisplayWorkStatus(null);
      setSelectedWorkStatuses(null);
      dispatch(appendAlert({
        severity:"error",
        message:"作業状況の取得に失敗しました。" + error.message,
        displayTime: 5000
      },));
    } finally {
      setIsLoading(false);
    }
  };

  const postFetchStatus = async (requestStatus: PostWorkStatus) => {
    const request = [requestStatus];
    try {
      await apiAxios.post(`/work_statuses`, request);
    } catch (error:any) {
      console.error(error);
      dispatch(appendAlert({
        severity:"error",
        message:"作業状況の更新に失敗しました。" + error.message,
        displayTime: 5000
      },));
    }
  };
  
  useEffect(() => {
    // 工場、加工、機械、所属、作業者のいずれかが変更されたら作業者の状態を更新する
    updateDisplayWorkStatus(null);
    setSelectedWorkStatuses(null);
    if(!(selectedProcCode === "") && !(selectedMachCode === "")){
      // 加工・機械が入力されたら作業者の状態を取得する
      getFetchStatus();
    }
  },[plantCode, selectedProcCode, selectedMachCode, selectedTeamCode, selectedMemberId]);

  useEffect(() => {
    if (selectedMemberId && selectedWorkStatuses !== null) {
      // 作業者
      const selectedWorkStatus = selectedWorkStatuses.find(d => d.member_id === selectedMemberId);
      if (selectedWorkStatus) {
        updateDisplayWorkStatus(selectedWorkStatus);
      } else {
        updateDisplayWorkStatus({
          plant_code: plantCode,
          plant_name: "",
          proc_code: selectedProcCode,
          proc_name: "",
          mach_code: selectedMachCode,
          mach_name: "",
          member_id: selectedMemberId,
          member_name: "",
          member_proc_code: selectedTeamCode,
          member_proc_name: "",
          status_code: 0,
          status_name: "",
          start_id: 0,
          suspension_id: 0,
          start_time: "",
          suspension_time: "",
          work_suspension_reason_code: 0,
          work_suspension_reason: "",
        });
      }
    } else {
      updateDisplayWorkStatus(null);
    }
  },[selectedWorkStatuses, selectedMemberId, plantCode, selectedProcCode, selectedMachCode, selectedTeamCode]);

  // ステータス時間編集モーダルの状態管理
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  // ステータス時間　テンプレート文字列
  const formatTime = (date: Date) => {
    const month = date.getMonth() + 1;
    const days = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${month}/${days} ${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  // 作業打刻ステータスの状態管理
  // 開始
  const startRequest = async () => {
    if (isPosting.current) {
      return;
    }
    try {
      isPosting.current = true;
      setIsLoading(true);
      const postWorkStatus: PostWorkStatus = {
        plant_code: plantCode,
        proc_code: selectedProcCode,
        mach_code: selectedMachCode,
        member_id: selectedMemberId,
        member_proc_code: selectedTeamCode,
        timestamp: dateToTimestampString(selectedDateTime), // 画面の開始時間を設定
        action: "start", // ★開始を設定
        start_id: selectedWorkStatus?.start_id || 0,
        suspension_id: selectedWorkStatus?.suspension_id || 0,
        work_suspension_reason_code: selectedWorkStatus?.work_suspension_reason_code || 0,
        update_by: userId
      };
      // 状態の更新をリクエスト
      await postFetchStatus(postWorkStatus);
      // 更新後の状態を取得
      await getFetchStatus();
    } finally {
      setIsLoading(false);
      isPosting.current = false;
    }
  };
  // 終了
  const stop = async () => {
    if (isPosting.current) {
      return;
    }
    try {
      isPosting.current = true;
      setIsLoading(true);
      const postWorkStatus: PostWorkStatus = {
        plant_code: plantCode,
        proc_code: selectedProcCode,
        mach_code: selectedMachCode,
        member_id: selectedMemberId,
        member_proc_code: selectedTeamCode,
        timestamp: dateToTimestampString(selectedDateTime), // 画面の終了時間を設定
        action: "end", // ★終了を設定
        start_id: selectedWorkStatus?.start_id || 0,
        suspension_id: selectedWorkStatus?.suspension_id || 0,
        work_suspension_reason_code: selectedWorkStatus?.work_suspension_reason_code || 0,
        update_by: userId
      };
      // 状態の更新をリクエスト
      await postFetchStatus(postWorkStatus);
      // 更新後の状態を取得
      await getFetchStatus();
    } finally {
      setIsLoading(false);
      isPosting.current = false;
    }

  };
  // 中断
  const interruption = async () => {
    if (selectedSuspendId === 0) {
      // 中断理由が選択されていない場合は処理を中断する
      console.log("中断理由未設定")
      return;
    }
    if (isPosting.current) {
      return;
    }
    try {
      isPosting.current = true;
      setIsLoading(true);
      const postWorkStatus: PostWorkStatus = {
        plant_code: plantCode,
        proc_code: selectedProcCode,
        mach_code: selectedMachCode,
        member_id: selectedMemberId,
        member_proc_code: selectedTeamCode,
        timestamp: dateToTimestampString(new Date()), // ★現在時刻を設定
        action: "suspend", // ★中断を設定
        start_id: selectedWorkStatus?.start_id || 0,
        suspension_id: selectedWorkStatus?.suspension_id || 0,
        work_suspension_reason_code: selectedSuspendId, // ★中断理由を設定
        update_by: userId
      };
      // 状態の更新をリクエスト
      await postFetchStatus(postWorkStatus);
      // 更新後の状態を取得
      await getFetchStatus();
    } finally {
      setIsLoading(false);
      isPosting.current = false;
    }

  };
  // 再開
  const resume = async () => {
    if (isPosting.current) {
      return;
    }
    try {
      isPosting.current = true;
      setIsLoading(true);
      const postWorkStatus: PostWorkStatus = {
        plant_code: plantCode,
        proc_code: selectedProcCode,
        mach_code: selectedMachCode,
        member_id: selectedMemberId,
        member_proc_code: selectedTeamCode,
        timestamp: dateToTimestampString(new Date()), // ★現在時刻を設定
        action: "resume", // ★再開を設定
        start_id: selectedWorkStatus?.start_id || 0,
        suspension_id: selectedWorkStatus?.suspension_id || 0,
        work_suspension_reason_code: selectedWorkStatus?.work_suspension_reason_code || 0,
        update_by: userId
      };
      // 状態の更新をリクエスト
      await postFetchStatus(postWorkStatus);
      // 更新後の状態を取得
      await getFetchStatus();
    } finally {
      setIsLoading(false);
      isPosting.current = false;
    }

  };

  return(
    <div className="timecard-container">
      <div className="timecard-modal-logo">
        <img src="images/time-card.svg" alt="" className="timecard-logo"/>
      </div>
      <div className="timecard-modal-mach-selection">
        <h1 className="timecard-title">機械選択</h1>
      </div>
      <div className="timecard-modal-proc"> {/* 加工 */}
        <div className="mach-selection">
          <img src="images/proc.svg" alt="" />
          <ProcCodeSelect label='加工' 
                plantCode={plantCode} 
                selectProcCode={selectedProcCode} 
                handleProcCodeChange={handleProcCodeChange} />
        </div>
      </div>
      <div className="timecard-modal-mach"> {/* 機械名 */}
        <div className="mach-selection">
          <img src="images/machine.svg" alt="" />
          <MachCodeSelect label='機械名'
                plantCode={plantCode}
                procCode={selectedProcCode}
                selectMachCode={selectedMachCode}
                handleMachCodeChange={handleMachCodeChange} />
        </div>
      </div>
      <div className="timecard-modal-team"> {/* 所属 */}
        <div className="mach-selection">
          <img src="images/team.svg" alt="" />
          <TeamSelect label='所属'
                plantCode={plantCode}
                selectProcCode={selectedTeamCode}
                handleMemberChange={handleTeamCodeChange} />
        </div>
      </div>
      <div className="timecard-modal-member"> {/* 作業者 */}
        <div className="mach-selection">
          <img src="images/operator.svg" alt="" />
          <MemberSelectWithWorkStatus label='作業者'
                selectMenberId={selectedMemberId}
                plantCode={plantCode}
                teamCode={selectedTeamCode}
                workStatuses={selectedWorkStatuses}
                handleMemberChange={handleMemberIdChange} />
        </div>
      </div>
   
      <div className="timecard-modal-work-status">
        <h1 className="timecard-title">作業者状況</h1>
      </div>
      <div className="timecard-modal-status">
        <div className="work-status"> {/* ステータス */}
          <label>
          {getDisplayStatus(selectedWorkStatus) === "unSelected" 
            || getDisplayStatus(selectedWorkStatus) === "loading" 
            || getDisplayStatus(selectedWorkStatus) === "idle" ? "開始時刻" : "終了時刻"}
          </label>
          <div className="timecard-clock">{formatTime(selectedDateTime)}</div>
          { getDisplayStatus(selectedWorkStatus) !== "unSelected" 
            && getDisplayStatus(selectedWorkStatus) !== "loading"
            && !(getDisplayStatus(selectedWorkStatus) === "working" && selectedSuspendId !== 0)
          ?
          <img src="images/edid_org.svg" alt="" className="work-status-edit" onClick={openModal}/>:
          <img src="images/edit_disabled.svg" alt="" className="work-status-edit" style={{cursor:"not-allowed"}}/>
          }
          <TimecardStatusModal isOpen={isModalOpen} onClose={closeModal} >
            <TimecardStatus date={selectedDateTime.toString()} updateDate={setSelectedDateTime} onClose={closeModal}/>
          </TimecardStatusModal>
        </div>
      </div>
      <div className="timecard-modal-reason">
        <div className="work-status"> {/* 中断理由 */}
            <SuspendReasonSelect label='中断理由'
                disabled= {getDisplayStatus(selectedWorkStatus) === "working" ? false: true}
                selectSuspendReasonCode={selectedSuspendId}
                handleSuspendIdChange={handleSuspendIdChange} />
        </div>
      </div>
      <div className="timecard-modal-status-display">
        <div className="work-status">{/* ステータス表示 */}
          {
          isLoading ?
            <Spinner />:
          getDisplayStatus(selectedWorkStatus) === "idle"?
            <p className="status-display">未作業</p>:
          getDisplayStatus(selectedWorkStatus) === "working"?
            <p className="status-display">作業中</p>:
          getDisplayStatus(selectedWorkStatus) === "suspend"?
            <p className="status-display">一時中断</p>:
          getDisplayStatus(selectedWorkStatus) === "resume"?
            <p className="status-display">作業再開</p>:
            <p></p>
          }
        </div>
      </div>
      <div className="timecard-modal-button">
        <div>
          <div className="work-status-block">
            {getDisplayStatus(selectedWorkStatus) === "idle"?
              <img src="images/mach-start.svg" alt="" className="work-status-button" onClick={startRequest}/>:
              <img src="images/mach-start-disabled.svg" alt="" className="work-status-button-disable"/>
            }
            <p>作業開始</p>
          </div>
          <div className="work-status-block">
            {(getDisplayStatus(selectedWorkStatus) === "working") 
              || (getDisplayStatus(selectedWorkStatus) === "suspend")
              || (getDisplayStatus(selectedWorkStatus) === "resume") ?
              <img src="images/mach-stop.svg" alt="" className="work-status-button" onClick={stop}/>:
              <img src="images/mach-stop-disabled.svg" alt="" className="work-status-button-disable"/>
            }
            <p>作業終了</p>
          </div>
          <div className="work-status-block">
            {(getDisplayStatus(selectedWorkStatus) === "working") && !(selectedSuspendId === 0)? 
              <img src="images/mach-pause.svg" alt="" className="work-status-button" onClick={interruption}/>:
              <img src="images/mach-pause-disabled.svg" alt="" className="work-status-button-disable"/>
            }
            <p>作業中断</p>
          </div>
          <div className="work-status-block">
            {getDisplayStatus(selectedWorkStatus) === "suspend"?
              <img src="images/restart.svg" alt="" className="work-status-button" onClick={resume}/>:
              <img src="images/restart_disabled.svg" alt="" className="work-status-button-disable"/>
            }
            <p>作業再開</p>
          </div>
        </div>
      </div>
      <div className="timecard-modal-close"></div>
    </div>
  );
};

export default Timecard;